/**
 * Deadline picker modal. Used from Tasks and ReviewQueue; `inline` renders the
 * same card without the overlay (the reschedule page).
 *
 * Availability-aware: it asks the backend which reminder slots are
 * already taken on the chosen day (other tasks' deadlines, busy blocks,
 * synced external calendars) and
 *   1. offers "next free slot" quick picks for today / tomorrow / in 2 days /
 *      in a week,
 *   2. shows the day's slots with busy ones marked,
 *   3. warns when the chosen time clashes (you can still set it).
 * There is deliberately no "No deadline": an active task always has one.
 *
 * Calendar invite: `onSelect(iso, { sendInvite })`. The second argument
 * tells the caller whether to ask the backend to email an .ics invite
 * for the new deadline. It follows the account setting — a checkbox in
 * "ask" mode, forced true in "always" mode, hidden/false in "never".
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, X, Check, CalendarPlus, AlertTriangle, Loader2, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import {
  useCalendarInviteMode, getRememberedInviteChoice, rememberInviteChoice,
} from "../lib/calendarPrefs";

const SLOT_MINUTES = 10;   // a task deadline is a 10-minute reminder slot

function nextQuarterHour(base = new Date()) {
  const d = new Date(base);
  const m = d.getMinutes();
  d.setMinutes(m + (15 - (m % 15)), 0, 0);
  return d;
}

function toDateInput(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function toTimeInput(d) {
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function combine(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  const dt = new Date(`${dateStr}T${timeStr}`);
  return isNaN(dt) ? null : dt;
}

function presetAt(daysOffset, hour = 9) {
  const d = new Date();
  d.setDate(d.getDate() + daysOffset);
  d.setHours(hour, 0, 0, 0);
  return d;
}

function formatPreview(d) {
  if (!d) return null;
  return d.toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
}

function fmtTime(hhmm) {
  const [h, m] = hhmm.split(":").map(Number);
  const d = new Date(); d.setHours(h, m, 0, 0);
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

const FALLBACK_PICKS = [
  { label: "Today", offset: 0, tone: "red" },
  { label: "Tomorrow", offset: 1, tone: "blue" },
  { label: "In 2 days", offset: 2, tone: "amber" },
  { label: "In a week", offset: 7, tone: "slate" },
];
const PICK_TONE = { "Today": "red", "Tomorrow": "blue", "In 2 days": "amber", "In a week": "slate" };

export default function DeadlinePicker({ onSelect, onCancel, currentDeadline = null, showInvite = true, taskId = null, inline = false }) {
  const inviteMode = useCalendarInviteMode();
  const [sendInvite, setSendInvite] = useState(getRememberedInviteChoice());
  useEffect(() => { rememberInviteChoice(sendInvite); }, [sendInvite]);
  const inviteOpt = () => {
    if (!showInvite || inviteMode === "never") return { sendInvite: undefined };
    if (inviteMode === "always") return { sendInvite: true };
    return { sendInvite };
  };

  const seed = useMemo(() => {
    if (currentDeadline) {
      const d = new Date(currentDeadline);
      if (!isNaN(d)) return d;
    }
    return nextQuarterHour();
  }, [currentDeadline]);

  const [dateStr, setDateStr] = useState(toDateInput(seed));
  const [timeStr, setTimeStr] = useState(toTimeInput(seed));
  const [avail, setAvail] = useState(null);          // day availability for dateStr
  const [availError, setAvailError] = useState(false);
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [suggestions, setSuggestions] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoadingAvail(true);
    api.get("/calendar/availability", { params: { date: dateStr, exclude_task: taskId || undefined } })
      .then((r) => {
        if (!alive) return;
        setAvail(r.data);
        setAvailError(false);
        if (suggestions === null) setSuggestions(r.data.suggestions || []);
      })
      .catch(() => { if (alive) { setAvail(null); setAvailError(true); if (suggestions === null) setSuggestions([]); } })
      .finally(() => { if (alive) setLoadingAvail(false); });
    return () => { alive = false; };
  }, [dateStr, taskId]); // eslint-disable-line react-hooks/exhaustive-deps

  const todayStr = toDateInput(new Date());
  const combined = combine(dateStr, timeStr);
  const preview = formatPreview(combined);

  // Clashes for the chosen time: anything busy overlapping [t, t+SLOT_MINUTES).
  const clashes = useMemo(() => {
    if (!combined || !avail?.busy) return [];
    const t0 = combined.getTime();
    const t1 = t0 + SLOT_MINUTES * 60000;
    return avail.busy.filter((b) => {
      const s = new Date(b.start).getTime();
      const e = new Date(b.end).getTime();
      return t0 < e && s < t1;
    });
  }, [combined, avail]);

  const outsideHours = useMemo(() => {
    if (!avail?.work_hours || !timeStr) return false;
    if (avail.is_work_day === false) return true;
    return timeStr < avail.work_hours.start || timeStr > avail.work_hours.end;
  }, [avail, timeStr]);

  const gridSlots = useMemo(() => (avail?.slots || []), [avail]);
  const freeCount = gridSlots.filter((s) => s.free && !s.past).length;

  const applyIso = (iso) => {
    const d = new Date(iso);
    setDateStr(toDateInput(d));
    setTimeStr(toTimeInput(d));
  };

  const commit = () => {
    if (!combined) return;
    onSelect(combined.toISOString(), inviteOpt());
  };

  const dayLabel = (() => {
    const d = combine(dateStr, "12:00");
    return d ? d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" }) : dateStr;
  })();

  return (
    <div
      className={inline ? "w-full" : "fixed inset-0 z-[60] bg-slate-900/40 backdrop-blur-[1px] grid place-items-center p-4 sw-fade-in"}
      onMouseDown={inline ? undefined : (e) => { if (e.target === e.currentTarget) onCancel(); }}
      data-testid={inline ? "deadline-picker-inline" : "deadline-picker-modal"}
    >
      <div className={inline
        ? "w-full bg-white rounded-xl border border-slate-200"
        : "w-full max-w-md bg-white rounded-xl shadow-2xl border border-slate-200 sw-pop max-h-[92vh] overflow-y-auto"}>
        {/* Header */}
        <div className="px-5 pt-5 pb-3 flex items-start gap-3 border-b border-slate-100">
          <div className="w-9 h-9 rounded-lg bg-blue-50 grid place-items-center shrink-0">
            <Calendar size={16} className="text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-[15.5px] font-bold text-slate-900 leading-snug">Set deadline</h2>
            <p className="text-[12.5px] text-slate-500 mt-0.5">Free slots are suggested from your other tasks and busy time.</p>
          </div>
          <button onClick={onCancel} aria-label="Close" className="h-7 w-7 grid place-items-center -mt-1 -mr-1 text-slate-400 hover:text-slate-900 rounded-lg hover:bg-slate-100 shrink-0">
            <X size={15} />
          </button>
        </div>

        {/* Quick picks — next free slot per day */}
        <div className="px-5 pt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-slate-500 inline-flex items-center gap-1.5">
              <Sparkles size={11} className="text-violet-500" /> Next free slot
            </div>
            {suggestions === null && <Loader2 size={12} className="animate-spin text-slate-400" />}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {suggestions && suggestions.length > 0 ? suggestions.map((s) => (
              <QuickPreset key={s.label} onClick={() => onSelect(s.iso, inviteOpt())} label={s.label} sub={s.time} tone={PICK_TONE[s.label] || "slate"} testid={`quick-${s.label.replace(/\s+/g, "-")}`} />
            )) : suggestions && FALLBACK_PICKS.map((p) => (
              <QuickPreset key={p.label} onClick={() => onSelect(presetAt(p.offset).toISOString(), inviteOpt())} label={p.label} sub="9:00 AM" tone={p.tone} />
            ))}
          </div>
          {suggestions && suggestions.length === 0 && !availError && (
            <p className="text-[11.5px] text-amber-700 mt-2">No free slots in the next week — your calendar is full. Pick a time below or free some time on the <Link to="/app/calendar" className="underline font-semibold">Calendar</Link>.</p>
          )}
        </div>

        {/* Custom date + time */}
        <div className="px-5 pt-5 pb-2">
          <div className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-2">Or pick specific</div>
          <div className="space-y-3">
            <div>
              <label className="text-[11.5px] font-semibold text-slate-600 flex items-center gap-1.5 mb-1">
                <Calendar size={11} className="text-slate-400" /> Date
              </label>
              <input type="date" value={dateStr} min={todayStr} onChange={(e) => setDateStr(e.target.value)}
                className="w-full h-10 px-3 border border-slate-200 rounded-lg text-[13.5px] focus:border-slate-900 outline-none" data-testid="deadline-date-input" />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[11.5px] font-semibold text-slate-600 flex items-center gap-1.5">
                  <Clock size={11} className="text-slate-400" /> Time
                </label>
                {avail && (
                  <span className="text-[11px] text-slate-400">
                    {avail.is_work_day === false
                      ? "Not a working day"
                      : loadingAvail ? "Checking…" : `${freeCount} free slot${freeCount === 1 ? "" : "s"} on ${dayLabel}`}
                  </span>
                )}
              </div>
              <input type="time" value={timeStr} step={900} onChange={(e) => setTimeStr(e.target.value)}
                className="w-full h-10 px-3 border border-slate-200 rounded-lg text-[13.5px] focus:border-slate-900 outline-none" data-testid="deadline-time-input" />

              {/* Slot grid for the day */}
              {avail && gridSlots.length > 0 && (
                <div className="mt-2 grid grid-cols-6 gap-1" data-testid="deadline-slots">
                  {gridSlots.map((s) => {
                    const isSel = timeStr === s.time;
                    const cls = isSel
                      ? "bg-slate-900 text-white border-slate-900"
                      : s.past
                        ? "bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed"
                        : s.free
                          ? "bg-white text-slate-700 border-slate-200 hover:border-emerald-500 hover:text-emerald-700"
                          : "bg-red-50 text-red-400 border-red-100 line-through hover:border-red-300";
                    const title = s.busy_with?.length
                      ? "Busy: " + s.busy_with.map((b) => (b.task_no ? `#${b.task_no} ` : "") + b.title).join(", ")
                      : s.past ? "Already passed" : "Free";
                    return (
                      <button key={s.time} type="button" disabled={s.past} onClick={() => setTimeStr(s.time)} title={title}
                        className={"h-7 rounded-md border text-[11px] font-medium transition " + cls}
                        data-testid={`slot-${s.time}`}>
                        {fmtTime(s.time).replace(":00", "").replace(" ", "")}
                      </button>
                    );
                  })}
                </div>
              )}
              {avail && avail.is_work_day === false && (
                <p className="text-[11.5px] text-slate-500 mt-2">Outside your working days. You can still pick a time here.</p>
              )}
              {availError && <p className="text-[11.5px] text-slate-400 mt-2">Couldn't check availability — pick a time as usual.</p>}
            </div>

            {/* Preview + clash warning */}
            {preview && (
              <div className={"text-[12.5px] rounded-lg px-3 py-2 border " + (clashes.length ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-slate-50 border-slate-100 text-slate-600")}>
                <span className={clashes.length ? "text-amber-700" : "text-slate-400"}>Will be set to:</span>{" "}
                <span className="font-semibold text-slate-900">{preview}</span>
                {clashes.length > 0 && (
                  <div className="mt-1.5 flex items-start gap-1.5 text-[12px]" data-testid="deadline-clash">
                    <AlertTriangle size={13} className="shrink-0 mt-0.5 text-amber-600" />
                    <span>
                      Clashes with {clashes.slice(0, 3).map((c, i) => (
                        <span key={c.id || i} className="font-semibold">{i > 0 ? ", " : ""}{c.task_no ? `#${c.task_no} ` : ""}{c.title} ({c.start_local}–{c.end_local})</span>
                      ))}{clashes.length > 3 ? ` and ${clashes.length - 3} more` : ""}. You can still set it, or pick a free slot above.
                    </span>
                  </div>
                )}
                {!clashes.length && outsideHours && avail?.work_hours && (
                  <div className="mt-1 text-[11.5px] text-slate-500">Outside your working hours ({fmtTime(avail.work_hours.start)}–{fmtTime(avail.work_hours.end)}).</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Calendar invite */}
        {showInvite && inviteMode !== "never" && (
          <div className="px-5 pt-1 pb-1">
            {inviteMode === "always" ? (
              <div className="flex items-center gap-2 text-[12px] text-slate-500 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2" data-testid="deadline-invite-always">
                <CalendarPlus size={13} className="text-blue-600 shrink-0" />
                A calendar invite will be emailed to you for this deadline.
              </div>
            ) : (
              <label className="flex items-start gap-2.5 cursor-pointer select-none rounded-lg border border-slate-200 hover:border-slate-300 px-3 py-2.5 transition" data-testid="deadline-invite-toggle">
                <input type="checkbox" checked={sendInvite} onChange={(e) => setSendInvite(e.target.checked)} className="mt-0.5 h-4 w-4 accent-blue-600" data-testid="deadline-invite-checkbox" />
                <span className="min-w-0">
                  <span className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-800">
                    <CalendarPlus size={13} className="text-blue-600" /> Email me a calendar invite
                  </span>
                  <span className="block text-[11.5px] text-slate-500 mt-0.5">Adds this deadline to Google, Outlook or Apple Calendar so your own calendar reminds you.</span>
                </span>
              </label>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="px-5 pt-3 pb-5 flex items-center justify-between gap-2 border-t border-slate-100 mt-4">
          <Link to="/app/calendar" className="text-[11.5px] text-slate-400 hover:text-blue-600 underline-offset-2 hover:underline">Block time or connect a calendar</Link>
          <div className="flex gap-2">
            <button onClick={onCancel} className="h-9 px-3.5 border border-slate-200 hover:border-slate-400 rounded-lg text-[13px] font-medium text-slate-700 transition" data-testid="deadline-cancel">Cancel</button>
            <button onClick={commit} disabled={!combined}
              className={"h-9 px-4 inline-flex items-center gap-1.5 text-white rounded-lg text-[13px] font-semibold disabled:opacity-50 transition active:scale-[0.98] " + (clashes.length ? "bg-amber-600 hover:bg-amber-700" : "bg-blue-600 hover:bg-blue-700")}
              data-testid="deadline-save">
              <Check size={14} /> {clashes.length ? "Set anyway" : "Set deadline"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const TONES = {
  red:   "border-red-200    bg-red-50    text-red-700    hover:border-red-300    hover:bg-red-100",
  blue:  "border-blue-200   bg-blue-50   text-blue-700   hover:border-blue-300   hover:bg-blue-100",
  amber: "border-amber-200  bg-amber-50  text-amber-700  hover:border-amber-300  hover:bg-amber-100",
  slate: "border-slate-200  bg-slate-50  text-slate-700  hover:border-slate-300  hover:bg-slate-100",
};

function QuickPreset({ onClick, label, sub, tone, testid }) {
  return (
    <button onClick={onClick} className={"h-14 px-3 border-2 rounded-lg text-left transition active:scale-[0.98] " + (TONES[tone] || TONES.slate)} data-testid={testid}>
      <div className="text-[13px] font-semibold leading-tight">{label}</div>
      <div className="text-[11px] opacity-75 mt-0.5">{sub}</div>
    </button>
  );
}
